import type {
  ThreadList,
  IndexThreadList,
  StateThreadList,
} from 'src/types/thread-lists';
import type {ThreadListAction} from 'src/action-creators/thread-lists';

import omit from 'lodash/omit';
import invariant from 'invariant';

import {
  RECEIVE_PAGE,
  RECEIVE_ONE,
  RECEIVE_SOME,
  RECEIVE_UPDATED,
  RECEIVE_BROADCASTS_FOR_THREAD,
  REMOVE_MEMBER,
  PAGE_LIMIT,
  REMOVE_ONE,
  ADD_ONE,
} from 'src/action-creators/thread-lists';
import {
  RECEIVE_BROADCAST,
  RECEIVE_BROADCASTS,
  RECEIVE_BROADCASTS_PAGINATED,
} from 'src/action-creators/broadcasts';

import type {Thread} from 'src/types/messages';


export type State = {
  list: string[];
  listDone: {
    [key: string]: boolean;
  };
  map: {
    [key: string]: StateThreadList;
  };
  threads: Thread[];
};

const initialState = {
  list: [],
  listDone: Object.freeze({}),
  map: Object.freeze({}),
  threads: [],
} as const;

const reducer = (
  // @ts-expect-error - TS2322 - Type '{ readonly list: readonly []; readonly listDone: Readonly<{}>; readonly map: Readonly<{}>; readonly threads: readonly []; }' is not assignable to type 'State'.
  state: State | null | undefined = initialState,
  action: ThreadListAction,
): State => {
  switch (action.type) {
    case RECEIVE_PAGE: {
      const {threadLists, offset, phone_number_set_id} = action.payload;
      // @ts-expect-error - TS2345 - Argument of type 'State | null' is not assignable to parameter of type 'State'.
      const map = reducePage(state, threadLists, offset, phone_number_set_id);
      // @ts-expect-error - TS2322 - Type '{ map: Record<string, any>; list: string[]; listDone: { [x: string]: boolean; }; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        map,
        list: Object.keys(map),
        listDone: {
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...state.listDone,
          [phone_number_set_id]: threadLists.length < PAGE_LIMIT,
        },
      };
    }

    case RECEIVE_ONE: {
      const threadList = action.payload;
      // @ts-expect-error - TS2322 - Type '{ map: { [x: string]: IndexThreadList | { phoneNumberSetId?: string; threadIds: string[]; internal: boolean; name: string; id: string; timeCreated: string; timeUpdated: string; }; }; threads: Thread[]; list?: string[] | undefined; listDone?: { ...; } | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        map: {
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...state.map,
          [threadList.id]: {
            ...omit(threadList, ['audienceMembers', 'contacts', 'threads']),
            threadIds: threadList.threads.map(({id}) => id),
            ...(threadList.threads[0]?.phoneNumberSet
              ? {
                  phoneNumberSetId: threadList.threads[0].phoneNumberSet,
                }
              : {}),
          },
        },
        // these threads are only to get the fullName and other details for broadcast chip, nothing to do with actual threads
        threads: threadList.threads,
      };
    }

    case ADD_ONE: {
      const {threadList, index = 0} = action.payload;

      // @ts-expect-error - TS2322 - Type '{ list: string[]; map: { [x: string]: IndexThreadList | { id: string; name: string; internal: boolean; timeCreated: string; timeUpdated: string; threadIds?: string[]; phoneNumberSetId?: string; }; }; listDone?: { ...; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        list: [
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...state.list.slice(0, index),
          threadList.id,
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS18047 - 'state' is possibly 'null'.
          ...state.list.slice(index, state.list.length),
        ],
        map: {
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...state.map,
          [threadList.id]: {
            ...threadList,
          },
        },
      };
    }

    case RECEIVE_SOME: {
      // @ts-expect-error - TS2322 - Type '{ map: { readonly [key: string]: IndexThreadList; }; list?: string[] | undefined; listDone?: { [key: string]: boolean; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        // @ts-expect-error - TS2345 - Argument of type 'State | null' is not assignable to parameter of type 'State'.
        map: reduceSome(state, action.payload),
      };
    }

    case RECEIVE_UPDATED: {
      const threadList = action.payload;
      // @ts-expect-error - TS2322 - Type '{ map: { [x: string]: IndexThreadList | { id: string; name: string; internal: boolean; timeCreated: string; timeUpdated: string; threadIds?: string[]; phoneNumberSetId?: string; }; }; list?: string[] | undefined; listDone?: { ...; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        map: {
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...state.map,
          [threadList.id]: {
            // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
            ...state.map[threadList.id],
            ...threadList,
          },
        },
      };
    }

    case REMOVE_MEMBER: {
      const {listId, threadId} = action.payload;

      // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
      const currentList = state.map[listId];

      invariant(
        currentList && currentList.threadIds,
        'Attempted to remove member from unloaded thread list.',
      );

      // @ts-expect-error - TS2322 - Type '{ map: { [x: string]: IndexThreadList | { threadIds: string[]; id: string; name: string; internal: boolean; timeCreated: string; timeUpdated: string; phoneNumberSetId?: string; }; }; list?: string[] | undefined; listDone?: { ...; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        map: {
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...state.map,
          [listId]: {
            ...currentList,
            threadIds: currentList.threadIds.filter((id) => id !== threadId),
          },
        },
      };
    }

    case REMOVE_ONE: {
      const {threadListId} = action.payload;
      // @ts-expect-error - TS2322 - Type '{ list: string[]; map: { [x: number]: IndexThreadList; }; listDone?: { [key: string]: boolean; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
        list: state.list.filter((id) => id !== threadListId) as string[],
        map: {
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...omit(state.map, [threadListId]),
        },
      };
    }

    case RECEIVE_BROADCASTS_FOR_THREAD: {
      const threadLists = action.payload.broadcasts.map((broadcast) => {
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
        let threadList = state.map[broadcast.threadListId] || {
          id: broadcast.threadListId,
          oneOnOne: true,
        };

        if (!threadList.threadIds) {
          threadList = {...threadList, threadIds: [action.payload.threadId]};
        }

        return threadList;
      });

      // @ts-expect-error - TS2322 - Type '{ map: { readonly [key: string]: IndexThreadList; }; list?: string[] | undefined; listDone?: { [key: string]: boolean; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        // @ts-expect-error - TS2345 - Argument of type 'State | null' is not assignable to parameter of type 'State'.
        map: reduceSome(state, threadLists),
      };
    }
    // @ts-expect-error - TS2678 - Type '"broadcasts/receiveBroadcasts"' is not comparable to type '"threads/lists/receivePage" | "threads/lists/receiveOne" | "threads/lists/receiveSome" | "threads/lists/receiveUpdated" | "threads/lists/receiveBroadcastsForThread" | "threads/lists/removeMember" | "threads/lists/removeOne" | "threads/lists/addOne"'.
    case RECEIVE_BROADCASTS: {
      // @ts-expect-error - TS2322 - Type '{ map: { readonly [key: string]: IndexThreadList; }; list?: string[] | undefined; listDone?: { [key: string]: boolean; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        // @ts-expect-error - TS2345 - Argument of type 'State | null' is not assignable to parameter of type 'State'. | TS2339 - Property 'payload' does not exist on type 'never'.
        map: reduceSome(state, action.payload.map(reduceBroadcastToThreadList)),
      };
    }
    // @ts-expect-error - TS2678 - Type '"broadcasts/receiveBroadcastsPaginated"' is not comparable to type '"threads/lists/receivePage" | "threads/lists/receiveOne" | "threads/lists/receiveSome" | "threads/lists/receiveUpdated" | "threads/lists/receiveBroadcastsForThread" | "threads/lists/removeMember" | "threads/lists/removeOne" | "threads/lists/addOne"'.
    case RECEIVE_BROADCASTS_PAGINATED: {
      // @ts-expect-error - TS2322 - Type '{ map: { readonly [key: string]: IndexThreadList; }; list?: string[] | undefined; listDone?: { [key: string]: boolean; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        map: reduceSome(
          // @ts-expect-error - TS2345 - Argument of type 'State | null' is not assignable to parameter of type 'State'.
          state,
          // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
          action.payload.broadcasts.map(reduceBroadcastToThreadList),
        ),
      };
    }
    // @ts-expect-error - TS2678 - Type '"broadcasts/receiveBroadcast"' is not comparable to type '"threads/lists/receivePage" | "threads/lists/receiveOne" | "threads/lists/receiveSome" | "threads/lists/receiveUpdated" | "threads/lists/receiveBroadcastsForThread" | "threads/lists/removeMember" | "threads/lists/removeOne" | "threads/lists/addOne"'.
    case RECEIVE_BROADCAST: {
      // @ts-expect-error - TS2322 - Type '{ map: { readonly [key: string]: IndexThreadList; }; list?: string[] | undefined; listDone?: { [key: string]: boolean; } | undefined; threads?: Thread[] | undefined; }' is not assignable to type 'State'.
      return {
        ...state,
        // @ts-expect-error - TS2345 - Argument of type 'State | null' is not assignable to parameter of type 'State'. | TS2339 - Property 'payload' does not exist on type 'never'.
        map: reduceSome(state, [reduceBroadcastToThreadList(action.payload)]),
      };
    }
  }

  // @ts-expect-error - TS2322 - Type 'State | null' is not assignable to type 'State'.
  return state;
};

export default reducer;

// @ts-expect-error - TS7006 - Parameter 'threadLists' implicitly has an 'any' type.
function reduceSome(state: State, threadLists) {
  const map = {...state.map} as const;

  for (const threadList of threadLists) {
    // @ts-expect-error - TS2542 - Index signature in type '{ readonly [key: string]: IndexThreadList; }' only permits reading.
    map[threadList.id] = {
      ...map[threadList.id],
      ...threadList,
    };
  }

  return map;
}

function reducePage(
  state: State,
  threadLists: Array<IndexThreadList>,
  offset: number,
  phone_number_set_id: string,
) {
  const oldLists = Object.entries(state.map);

  const filtredLists =
    offset === 0
      ? oldLists.filter(
          ([id, threadList]: [any, any]) =>
            phone_number_set_id !== threadList.phoneNumberSetId,
        )
      : oldLists;

  const map = filtredLists.reduce<Record<string, any>>(
    (map, [id, threadList]: [any, any]) => {
      map[id] = threadList;
      return map;
    },
    {},
  );

  for (const threadList of threadLists) {
    map[threadList.id] = {
      ...map[threadList.id],
      ...threadList,
    };
  }

  return map;
}

// @ts-expect-error - TS7031 - Binding element 'threadListId' implicitly has an 'any' type. | TS7031 - Binding element 'threadIds' implicitly has an 'any' type. | TS7031 - Binding element 'name' implicitly has an 'any' type.
function reduceBroadcastToThreadList({threadListId, threadIds, name}) {
  return {
    id: threadListId,
    threadIds,
    name,
  };
}
